import { Platform } from "react-native";
import { BaseService } from "./BaseService";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useConfig } from "../hooks/useConfig";
import { nanoid } from "nanoid/non-secure";
import { ApiError } from "../utils/error";
import { translate } from "../i18n";
import { eProtect } from "../utils/eProtect-api2";

const config = useConfig();

export class VantivEProtectService extends BaseService {
  constructor(
    public cognitoUser: CognitoUser,
    public dispatch = (tokens: any) => {},
    public url = config.vantivPaypageUrl
  ) {
    super(url, cognitoUser, dispatch);
  }

  async createPaypageRegistration(accountNumber: string, cvv: string) {
    const orderId = nanoid(8);
    const id = nanoid(3);

    this.logger.log(`Sending request to eProtect orderId = ${orderId}, id = ${id}`);

    let eProtectResponse = {
      message: "Fail",
      paypageRegistrationId: "",
      response: "",
      lastFour: "",
      bin: "",
      type: "",
    };

    const eProtectCallBack = (item: any) => {
      // console.log("eProtectCallBack", item);
    };

    try {
      if (Platform.OS === "web") {
        const vantivModulus =
          config.env == "prd"
            ? "888b15986deae1769d34e605e9d1c0233b24593fca0c7490a30ce78530bfb90f779905bb78ae187e87f763df10303b0d9267cad57738041749c70d977943eea98d3860a392362efa9445a44b5d77487bf6af5e6bd2c8d6b3e9a1a02a2d75cb6c312cb7309e70f6924ede291b706cf49c0a31258729ba4bdf1ed63b2d2038dfbb27da376fc0c23c7b8635de97ddb295ec54e93b20787f469a75d4a177ede79afd03ed84d74f2baae5d92d27d0d7eba0b9cf1bcf85d7fcf6410f03f579829681145282504f4b2ed92399c65eba8b21075aabd700ce830f488c1201e7b6a9b8f3eb6f9247dc55104a5d89733b10e29f3dfc0ab5e2175a7915fb0d56655a91f1ff59"
            : "bc637dd74ba76507dad5af1c7ad6f97dbef5298c3b9f74caea7301347db7b4a8c37f26491863100667246fd45071f3346bf62239f9b117d06fb67861b66ad0d158beeddd2f6f28be93d846f4c8f9ba1bd7e8f186f36cab0e432f22b3d732c221a9ff00a9bfacb88b24503e2695fd7237835d4936477b21289478906a49b164f52503c20eb29f11fcbda2af94deb9a0bfde5a4589276897436315c5d664d60bf10650164f509283aed39747ad5d6cb2bbe54e1b42306e5db37dfd42dcbfcc689e0ddfe3bc9cb22ae7018e5a4a1ff39813584ac7bd6d6d65ca763f0a672da454081ea20e8b1d403316d80b9353ba396bea8962b1a7d2f775c76612d857c1f7594f";
        const vantivExponent = config.env == "prd" ? "10001" : "10001";
        const vantivKeyId = config.env == "prd" ? "26348800017" : "1";
        const vantivVisaCheckoutApiKey =
          config.env == "prd"
            ? "YIVDE0GZEE18VM6BFYIS218dbXdWD-42bCYl0m0KPUxgix_qE"
            : "F7LWOL8T9NPSN27LTG8N21WzEzLI_CreyFvsTNTroL-naw1CE";
        const vantivVisaCheckoutEncryptionKey =
          config.env == "prd"
            ? "GOR4DEIB9H0A0FYVZQJC14N4-6kbAF8FZi58EWWIMcv0IkSPQ"
            : "5M6PIKT7XURYXV0ZXFMB13RsoW6GlkJCB12FRYGolGw8B3-eM";

        const valuesForCall = eProtect(
          config.vantivPaypageUrl,
          vantivModulus,
          vantivExponent,
          vantivKeyId,
          vantivVisaCheckoutApiKey,
          vantivVisaCheckoutEncryptionKey
        ).getValuesForCall(
          {
            id,
            request_paypageId: config.paypageId,
            request_reportGroup: config.reportGroup,
            request_merchantTxnId: orderId,
            orderId,
            reportGroup: config.reportGroup,
            url: config.vantivPaypageUrl,
            paypageId: config.paypageId,
          },
          {
            accountNum: {
              value: accountNumber,
            },
            cvv2: {
              value: cvv,
            },
          },
          eProtectCallBack,
          eProtectCallBack,
          eProtectCallBack,
          15000
        );

        eProtectResponse = await this.makeGETJSONPSimpleRequest("eProtect/paypage", valuesForCall);
      } else {
        const body = new URLSearchParams({
          reportGroup: config.reportGroup,
          paypageId: config.paypageId,
          orderId,
          id,
          accountNumber,
          cvv,
        });
        const controller = new AbortController();
        const { signal } = controller;

        setTimeout(() => {
          controller.abort();
        }, 15000);

        eProtectResponse = await this.makePostSimpleRequest(
          "eProtect/paypage",
          body.toString(),
          "application/x-www-form-urlencoded",
          signal
        );
      }

      this.logger.logResponse(this.url + "/eProtect/paypage", eProtectResponse);
    } catch (e) {
      this.logger.error(e);

      const error = translate("addPaymentCCDebit.errors.eProtectContactSupport") || "";
      throw new ApiError(error, 500);
    }

    if (eProtectResponse.message === "Success") {
      return eProtectResponse;
    } else {
      let error: string | null;
      switch (eProtectResponse.response) {
        case "871":
        case "872":
        case "873":
        case "875":
        case "876":
        case "881":
        case "882":
        case "883":
          error = translate("addPaymentCCDebit.errors.eProtectVerifyError");
          break;
        default:
          error = translate("addPaymentCCDebit.errors.eProtectContactSupport");
      }

      throw new ApiError(error || "", eProtectResponse.response);
    }
  }
}
